// /var/www/afra/web-design.rampagesoft.com/my-app/src/metatag/MetaTagContentDetail.js

import { useEffect, useMemo } from 'react';

const stripHtml = (html) => {
  if (!html) return ''; // ป้องกัน error ถ้า html เป็น null หรือ undefined
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const MetaTagContentDetail = ({ content, currentUrl, imageUrl }) => {
  // ตรวจสอบเพื่อให้แน่ใจว่า content ไม่ใช่ null หรือ undefined ก่อนเข้าถึง property
  // ContentDetail.js ควรจะจัดการเรื่องนี้แล้ว แต่เป็นการป้องกันเพิ่มเติม
  const pageTitle = content?.Home_Sub || 'Untitled Article';
  const pageDescription = stripHtml(content?.Home_Short || '').substring(0, 160) || "ไม่มีคำอธิบาย";
  const publishedDate = content?.Home_Date || new Date().toISOString();
  const effectiveImageUrl = imageUrl || 'https://afaa.website/s/ff6970.png';

  const structuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": pageTitle,
    "description": pageDescription,
    "datePublished": publishedDate,
    "image": effectiveImageUrl,
    "url": currentUrl,
    "author": {
      "@type": "Organization",
      "name": "AFRA APACHE"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AFRA APACHE",
      "logo": {
        "@type": "ImageObject",
        "url": "https://afaa.website/s/ff6970.png"
      }
    }
  }), [pageTitle, pageDescription, publishedDate, effectiveImageUrl, currentUrl]);

  const sitemapSchema = useMemo(() => `
    <?xml version="1.0" encoding="UTF-8"?>
    <urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
      <url>
        <loc>${currentUrl}</loc>
        <lastmod>${publishedDate}</lastmod>
        <changefreq>weekly</changefreq>
        <priority>0.8</priority>
      </url>
    </urlset>
  `, [currentUrl, publishedDate]);

  useEffect(() => {
    // console.log('MetaTagContentDetail props:', { content, currentUrl, imageUrl }); // สามารถคงไว้เพื่อ debug

    // ไม่จำเป็นต้องมี console.warn ที่นี่อีกแล้ว เพราะ ContentDetail.js
    // ควรจะส่ง content ที่มี Home_Sub ที่ถูกต้อง (หรือ default ที่ตั้งไว้แล้ว) มาให้
    // if (!content?.Home_Sub) {
    //   console.warn('Warning: content.Home_Sub is null or undefined, using default "Untitled Article"');
    // }

    // Skip meta tag updates if content is not ready (หรือถ้า content object ไม่มีอยู่)
    // ContentDetail.js มีการเช็ค {content && (...)} ก่อน render component นี้แล้ว
    // แต่การเช็คซ้ำซ้อนอีกชั้นก็ไม่เสียหาย เพื่อความ robust ของ component นี้เอง
    if (!content || !currentUrl) { // เพิ่มการตรวจสอบ currentUrl ด้วย
        console.warn('MetaTagContentDetail: content or currentUrl is missing, skipping meta tag updates.');
        return;
    }

    const head = document.head;
    const existingTagsQuery = '[data-meta-tag="content-detail"]';

    // Remove existing meta tags added by this component
    document.querySelectorAll(existingTagsQuery).forEach(tag => tag.remove());

    // Document Title
    // ตรวจสอบว่ามี <title> tag อยู่แล้วหรือไม่ ถ้ามี ให้อัปเดต content แทนการสร้างใหม่
    let titleTag = document.querySelector('title');
    if (!titleTag) {
        titleTag = document.createElement('title');
        head.appendChild(titleTag);
    }
    titleTag.textContent = pageTitle;
    titleTag.setAttribute('data-meta-tag', 'content-detail'); // เพิ่ม data-meta-tag เพื่อให้ cleanup ทำงานได้

    // Meta description
    const metaDesc = document.createElement('meta');
    metaDesc.name = 'description';
    metaDesc.content = pageDescription;
    metaDesc.setAttribute('data-meta-tag', 'content-detail');
    head.appendChild(metaDesc);

    // Sitemap Schema
    const sitemapScript = document.createElement('script');
    sitemapScript.type = 'application/xml'; // Note: sitemap is XML, not ld+json. Usually linked, not embedded.
                                             // For embedding, application/ld+json for Schema.org is more common.
                                             // If this is meant for something else, type might be okay.
    sitemapScript.textContent = sitemapSchema;
    sitemapScript.setAttribute('data-meta-tag', 'content-detail');
    head.appendChild(sitemapScript);

    // Structured Data (Schema.org Article)
    const jsonLdScript = document.createElement('script');
    jsonLdScript.type = 'application/ld+json';
    jsonLdScript.textContent = JSON.stringify(structuredData);
    jsonLdScript.setAttribute('data-meta-tag', 'content-detail');
    head.appendChild(jsonLdScript);

    const metaTagsConfig = [
      { tag: 'link', rel: 'icon', type: 'image/png', href: 'https://afaa.website/s/ff6970.png' },
      { tag: 'meta', httpEquiv: 'revisit-after', content: '3 days' },
      { tag: 'meta', httpEquiv: 'Cache-control', content: 'public' },
      { tag: 'meta', name: 'copyright', content: 'AFRA APACHE' },
      { tag: 'meta', name: 'owner', content: 'AFRA APACHE' },
      { tag: 'meta', name: 'web_author', content: 'AFRA APACHE' },
      { tag: 'meta', name: 'author', content: 'AFRA APACHE' },
      { tag: 'meta', name: 'generator', content: 'react' },
      { tag: 'meta', name: 'distribution', content: 'Global' },
      { tag: 'meta', name: 'rating', content: 'General' },
      { tag: 'meta', name: 'expires', content: 'Never' },
      
      { tag: 'link', rel: 'canonical', href: currentUrl },
      { tag: 'meta', property: 'og:title', content: pageTitle },
      { tag: 'meta', property: 'og:description', content: pageDescription },
      { tag: 'meta', property: 'og:type', content: 'article' },
      { tag: 'meta', property: 'og:url', content: currentUrl },
      { tag: 'meta', property: 'og:image', content: effectiveImageUrl },
      { tag: 'meta', property: 'og:site_name', content: 'web-design.rampagesoft.com' },
      { tag: 'meta', property: 'og:locale', content: 'th_TH' },
      { tag: 'meta', property: 'og:image:secure_url', content: effectiveImageUrl },
      { tag: 'meta', name: 'twitter:card', content: 'summary' },
      { tag: 'meta', name: 'twitter:site', content: '@AFRAAPACHE' },
      { tag: 'meta', name: 'twitter:domain', content: currentUrl }, // Consider window.location.hostname
      { tag: 'meta', name: 'twitter:title', content: pageTitle },
      { tag: 'meta', name: 'twitter:description', content: pageDescription },
      { tag: 'meta', name: 'twitter:image', content: effectiveImageUrl },
    ];

    metaTagsConfig.forEach(({ tag, ...attributes }) => {
      const element = document.createElement(tag);
      Object.entries(attributes).forEach(([key, value]) => {
        if(value === undefined || value === null) return; // Skip attributes with no value
        if (key === 'httpEquiv') {
          element.setAttribute('http-equiv', value);
        } else {
          element.setAttribute(key, value);
        }
      });
      element.setAttribute('data-meta-tag', 'content-detail');
      head.appendChild(element);
    });

    return () => {
      document.querySelectorAll(existingTagsQuery).forEach(tag => tag.remove());
      // หาก title tag เดิมไม่ได้ถูกสร้างโดย component นี้ ก็ไม่ควรลบ content ของมัน
      // แต่ถ้าเรา set data-meta-tag ให้ title ด้วย ก็จะถูกลบไปพร้อมกัน
      // หรือจะ reset title กลับไปเป็นค่า default ของเว็บก็ได้
      // document.title = "Default Site Title"; // ตัวอย่าง
    };
    // Dependencies: ใช้ค่าที่คำนวณแล้วเพื่อให้ useEffect ทำงานเมื่อค่าเหล่านี้เปลี่ยนจริงๆ
  }, [pageTitle, pageDescription, publishedDate, effectiveImageUrl, currentUrl, sitemapSchema, structuredData, content]); // เพิ่ม content เข้าไปเพื่อให้ re-run ถ้า object content เปลี่ยน

  return null; // This component only manipulates the <head>
};

export default MetaTagContentDetail;